import { useState, useEffect, useMemo } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Payment } from '../types';
import { collection, query, where, orderBy, onSnapshot, Timestamp } from 'firebase/firestore';
import { db } from '../lib/firebase';
import { Card } from '../components/ui/Card';
import { Gift, User, Scissors, CreditCard, Wallet, DollarSign, Building2, Smartphone, Clock, FileText, AlertTriangle, Calendar } from 'lucide-react';
import toast from 'react-hot-toast';

export default function BarberPaymentsPage() {
  const { user } = useAuth();
  const [payments, setPayments] = useState<Payment[]>([]);
  const [loading, setLoading] = useState(true);

  // Filters - same as admin
  const [selectedPaymentMethods, setSelectedPaymentMethods] = useState<string[]>([]);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [selectedMonth, setSelectedMonth] = useState<string>('');

  useEffect(() => {
    if (!user?.id) return;

    setLoading(true);

    // Real-time listener for barber payments
    const q = query(
      collection(db, 'payments'),
      where('barberUserId', '==', user.id),
      orderBy('completedAt', 'desc')
    );

    const unsubscribe = onSnapshot(q, (snapshot) => {
      const data = snapshot.docs.map(doc => ({
        ...doc.data(),
        paymentId: doc.id,
      })) as Payment[];

      setPayments(data);
      setLoading(false);
    }, (error) => {
      console.error('[BarberPayments] Error loading payments:', error);
      toast.error('Error al cargar los pagos');
      setLoading(false);
    });

    return () => unsubscribe();
  }, [user?.id]);

  // Generate available months from payments (same as admin)
  const availableMonths = useMemo(() => {
    const months = new Set<string>();
    payments.forEach(p => {
      if (p.completedAt) {
        const date = p.completedAt.toDate();
        const monthKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
        months.add(monthKey);
      }
    });
    return Array.from(months).sort().reverse();
  }, [payments]);

  const getMonthLabel = (monthKey: string) => {
    const [year, month] = monthKey.split('-');
    const date = new Date(parseInt(year), parseInt(month) - 1, 1);
    return date.toLocaleDateString('es-ES', { month: 'long', year: 'numeric' });
  };

  const handleMonthSelect = (monthKey: string) => {
    setSelectedMonth(monthKey);
    if (monthKey) {
      const [year, month] = monthKey.split('-');
      const startOfMonth = new Date(parseInt(year), parseInt(month) - 1, 1);
      const endOfMonth = new Date(parseInt(year), parseInt(month), 0);
      setStartDate(startOfMonth.toISOString().split('T')[0]);
      setEndDate(endOfMonth.toISOString().split('T')[0]);
    } else {
      setStartDate('');
      setEndDate('');
    }
  };

  // Filter payments (same logic as admin)
  const filteredPayments = payments.filter(p => {
    // Payment method filter
    if (selectedPaymentMethods.length > 0 && !selectedPaymentMethods.includes(p.paymentMethod)) {
      return false;
    }

    // Date range filter
    if (startDate || endDate) {
      const paymentDate = p.completedAt?.toDate();
      if (!paymentDate) return false;

      const start = startDate ? new Date(startDate) : null;
      const end = endDate ? new Date(endDate) : null;

      if (start && paymentDate < start) return false;
      if (end) {
        end.setHours(23, 59, 59, 999);
        if (paymentDate > end) return false;
      }
    }

    return true;
  });

  // Calculate totals (identical to admin)
  const totalPayments = filteredPayments.length;
  const totalAmount = filteredPayments.reduce((sum, p) => sum + p.totalAmount, 0);
  const totalPaid = filteredPayments.reduce((sum, p) => sum + p.paidAmount, 0);
  const totalVoucherDiscount = filteredPayments
    .reduce((sum, p) => sum + (p.voucherDiscount || (p as any).discountAmount || 0), 0);

  // Payment method breakdown (same as admin)
  const paymentsByMethod = filteredPayments.reduce((acc, p) => {
    acc[p.paymentMethod] = (acc[p.paymentMethod] || 0) + p.paidAmount;
    return acc;
  }, {} as Record<string, number>);

  const formatDate = (timestamp: Timestamp) => {
    const date = timestamp.toDate();
    return new Intl.DateTimeFormat('es-ES', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    }).format(date);
  };

  const getPaymentMethodIcon = (method: Payment['paymentMethod']) => {
    switch (method) {
      case 'voucher':
        return <Gift className="w-4 h-4 text-accent" />;
      case 'card':
        return <CreditCard className="w-4 h-4 text-info" />;
      case 'cash':
        return <Wallet className="w-4 h-4 text-success" />;
      case 'transfer':
        return <Building2 className="w-4 h-4 text-accent" />;
      case 'bizum':
        return <Smartphone className="w-4 h-4 text-accent" />;
      default:
        return null;
    }
  };

  const getPaymentMethodText = (method: Payment['paymentMethod']) => {
    switch (method) {
      case 'voucher':
        return 'Vale';
      case 'card':
        return 'Tarjeta';
      case 'cash':
        return 'Efectivo';
      case 'transfer':
        return 'Transferencia';
      case 'bizum':
        return 'Bizum';
      default:
        return '-';
    }
  };

  const paymentMethodOptions = [
    { value: 'voucher', label: 'Vale', icon: Gift, color: 'amber' },
    { value: 'card', label: 'Tarjeta', icon: CreditCard, color: 'blue' },
    { value: 'cash', label: 'Efectivo', icon: Wallet, color: 'green' },
    { value: 'transfer', label: 'Transferencia', icon: Building2, color: 'purple' },
    { value: 'bizum', label: 'Bizum', icon: Smartphone, color: 'pink' },
  ];

  const togglePaymentMethod = (method: string) => {
    setSelectedPaymentMethods(prev =>
      prev.includes(method)
        ? prev.filter(m => m !== method)
        : [...prev, method]
    );
  };

  const clearFilters = () => {
    setSelectedPaymentMethods([]);
    setStartDate('');
    setEndDate('');
    setSelectedMonth('');
  };

  if (loading) {
    return (
      <div className="p-6">
        <div className="animate-pulse space-y-4">
          <div className="h-8 bg-surface-2 rounded w-1/3"></div>
          <div className="h-64 bg-surface-2 rounded"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-ink">
          Mi Facturación
        </h1>
        <p className="text-sm text-ink-muted mt-1">
          Historial completo de pagos recibidos
        </p>
      </div>

      {/* Filters */}
      <Card className="p-4 space-y-4">
        {/* Month selector */}
        {availableMonths.length > 0 && (
          <div>
            <label className="block text-sm font-medium text-ink-muted mb-2">
              <Calendar className="w-4 h-4 inline mr-1" />
              Mes
            </label>
            <div className="flex flex-wrap gap-2">
              <button
                onClick={() => handleMonthSelect('')}
                className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                  !selectedMonth
                    ? 'bg-info text-white'
                    : 'bg-surface-2 text-ink-muted hover:bg-surface-3'
                }`}
              >
                Todos
              </button>
              {availableMonths.map(month => (
                <button
                  key={month}
                  onClick={() => handleMonthSelect(month)}
                  className={`px-3 py-1.5 rounded-lg text-sm font-medium capitalize transition-colors ${
                    selectedMonth === month
                      ? 'bg-info text-white'
                      : 'bg-surface-2 text-ink-muted hover:bg-surface-3'
                  }`}
                >
                  {getMonthLabel(month)}
                </button>
              ))}
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {/* Date range */}
          <div>
            <label className="block text-sm font-medium text-ink-muted mb-2">
              Desde
            </label>
            <input
              type="date"
              value={startDate}
              onChange={(e) => { setStartDate(e.target.value); setSelectedMonth(''); }}
              className="w-full px-3 py-2 border border-hairline rounded-lg bg-surface text-ink focus:ring-2 focus:ring-info"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-ink-muted mb-2">
              Hasta
            </label>
            <input
              type="date"
              value={endDate}
              onChange={(e) => { setEndDate(e.target.value); setSelectedMonth(''); }}
              className="w-full px-3 py-2 border border-hairline rounded-lg bg-surface text-ink focus:ring-2 focus:ring-info"
            />
          </div>
          <div className="flex items-end">
            <button
              onClick={clearFilters}
              className="px-4 py-2 text-sm font-medium text-ink-muted bg-surface border border-hairline rounded-lg hover:bg-surface-3 transition-colors"
            >
              Limpiar Filtros
            </button>
          </div>
        </div>

        {/* Payment method filter */}
        <div>
          <label className="block text-sm font-medium text-ink-muted mb-2">
            Método de pago
          </label>
          <div className="flex flex-wrap gap-2">
            {paymentMethodOptions.map(option => {
              const Icon = option.icon;
              const isSelected = selectedPaymentMethods.includes(option.value);
              return (
                <button
                  key={option.value}
                  onClick={() => togglePaymentMethod(option.value)}
                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium transition-colors border ${
                    isSelected
                      ? `bg-${option.color}-100 dark:bg-${option.color}-900/30 border-${option.color}-500 text-${option.color}-700 dark:text-${option.color}-300`
                      : 'bg-surface border-hairline text-ink-muted hover:bg-surface-3'
                  }`}
                >
                  <Icon className="w-4 h-4" />
                  {option.label}
                </button>
              );
            })}
          </div>
        </div>
      </Card>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="p-4 border-info/30">
          <div className="flex items-center gap-3">
            <div className="p-3 bg-info/100/20 rounded-lg">
              <DollarSign className="w-6 h-6 text-info" />
            </div>
            <div>
              <p className="text-sm text-ink-muted">Total Pagos</p>
              <p className="text-2xl font-bold text-ink">{totalPayments}</p>
            </div>
          </div>
        </Card>

        <Card className="p-4 border-success/30">
          <div className="flex items-center gap-3">
            <div className="p-3 bg-success/100/20 rounded-lg">
              <Wallet className="w-6 h-6 text-success" />
            </div>
            <div>
              <p className="text-sm text-ink-muted">Total Servicios</p>
              <p className="text-2xl font-bold text-ink">
                {(totalAmount / 100).toFixed(2)}€
              </p>
            </div>
          </div>
        </Card>

        <Card className="p-4 border-info/30">
          <div className="flex items-center gap-3">
            <div className="p-3 bg-info/100/20 rounded-lg">
              <CreditCard className="w-6 h-6 text-info" />
            </div>
            <div>
              <p className="text-sm text-ink-muted">Total Cobrado</p>
              <p className="text-2xl font-bold text-ink">
                {(totalPaid / 100).toFixed(2)}€
              </p>
            </div>
          </div>
        </Card>

        <Card className="p-4 border-accent/30">
          <div className="flex items-center gap-3">
            <div className="p-3 bg-accent/100/20 rounded-lg">
              <Gift className="w-6 h-6 text-accent" />
            </div>
            <div>
              <p className="text-sm text-ink-muted">Desc. Vales</p>
              <p className="text-2xl font-bold text-ink">
                {(totalVoucherDiscount / 100).toFixed(2)}€
              </p>
            </div>
          </div>
        </Card>
      </div>

      {/* Payment method breakdown */}
      {Object.keys(paymentsByMethod).length > 0 && (
        <Card className="p-4">
          <h3 className="text-sm font-medium text-ink-muted mb-3">Desglose por método de pago</h3>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
            {Object.entries(paymentsByMethod).map(([method, amount]) => (
              <div key={method} className="flex items-center gap-2 p-2 rounded-lg bg-surface-2">
                {getPaymentMethodIcon(method as Payment['paymentMethod'])}
                <div>
                  <p className="text-xs text-ink-subtle">{getPaymentMethodText(method as Payment['paymentMethod'])}</p>
                  <p className="text-sm font-semibold text-ink">{(amount / 100).toFixed(2)}€</p>
                </div>
              </div>
            ))}
          </div>
        </Card>
      )}

      {/* Table */}
      {filteredPayments.length === 0 ? (
        <Card className="p-12 text-center">
          <DollarSign className="w-16 h-16 text-ink-subtle mx-auto mb-4" />
          <p className="text-ink-muted">
            No hay pagos registrados{startDate || endDate ? ' en el período seleccionado' : ''}
          </p>
        </Card>
      ) : (
        <Card className="overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-surface-2">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-ink-subtle uppercase tracking-wider">
                    Fecha
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-ink-subtle uppercase tracking-wider">
                    Cliente
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-ink-subtle uppercase tracking-wider">
                    Servicio
                  </th>
                  <th className="px-4 py-3 text-center text-xs font-medium text-ink-subtle uppercase tracking-wider">
                    Duración
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-ink-subtle uppercase tracking-wider">
                    Precio
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-ink-subtle uppercase tracking-wider">
                    Desc. Vale
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-ink-subtle uppercase tracking-wider">
                    Cobrado
                  </th>
                  <th className="px-4 py-3 text-center text-xs font-medium text-ink-subtle uppercase tracking-wider">
                    Método
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-ink-subtle uppercase tracking-wider">
                    Notas
                  </th>
                </tr>
              </thead>
              <tbody className="bg-surface divide-y divide-hairline">
                {filteredPayments.map((payment) => (
                  <tr key={payment.paymentId} className="hover:bg-surface-2 transition-colors">
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-ink">
                      {formatDate(payment.completedAt)}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      <div className="flex items-center gap-2">
                        <User className="w-4 h-4 text-ink-subtle" />
                        <span className="text-sm font-medium text-ink">
                          {payment.clientName}
                        </span>
                      </div>
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      <div className="flex items-center gap-2">
                        <Scissors className="w-4 h-4 text-ink-subtle" />
                        <span className="text-sm text-ink">
                          {payment.serviceName}
                        </span>
                      </div>
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-center">
                      {payment.serviceDuration ? (
                        <div className="flex items-center justify-center gap-1 text-sm text-ink-muted">
                          <Clock className="w-3 h-3" />
                          {payment.serviceDuration}min
                        </div>
                      ) : (
                        <span className="text-sm text-ink-subtle">-</span>
                      )}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-right text-sm">
                      <div className="flex flex-col items-end">
                        <span className="text-ink">
                          {(payment.totalAmount / 100).toFixed(2)}€
                        </span>
                        {(payment as any).priceWasAdjusted && (payment as any).originalServicePrice && (
                          <span className="text-xs text-ink-subtle line-through">
                            {((payment as any).originalServicePrice / 100).toFixed(2)}€
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-right text-sm font-semibold text-accent">
                      {(payment.voucherDiscount || (payment as any).discountAmount) ? (
                        <>-{((payment.voucherDiscount || (payment as any).discountAmount) / 100).toFixed(2)}€</>
                      ) : (
                        <span className="text-ink-subtle">-</span>
                      )}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-right text-sm font-semibold text-success">
                      {(payment.paidAmount / 100).toFixed(2)}€
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-center">
                      <div className="flex items-center justify-center gap-1">
                        {getPaymentMethodIcon(payment.paymentMethod)}
                        <span className="text-xs text-ink-muted">
                          {getPaymentMethodText(payment.paymentMethod)}
                        </span>
                      </div>
                    </td>
                    <td className="px-4 py-3 max-w-xs">
                      {payment.notes ? (
                        <div className="flex items-start gap-2">
                          {(payment as any).priceWasAdjusted ? (
                            <span title="Precio ajustado">
                              <AlertTriangle className="w-4 h-4 text-accent flex-shrink-0 mt-0.5" />
                            </span>
                          ) : (
                            <FileText className="w-4 h-4 text-ink-subtle flex-shrink-0 mt-0.5" />
                          )}
                          <span className="text-xs text-ink-muted line-clamp-2" title={payment.notes}>
                            {payment.notes}
                          </span>
                        </div>
                      ) : (
                        <span className="text-xs text-ink-subtle">-</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}
    </div>
  );
}
